/**
 * Microsoft Graph Mail Service
 * Sends emails using the logged-in user's Microsoft account via Graph API
 */

const GRAPH_MAIL_ENDPOINT = "https://graph.microsoft.com/v1.0/me/sendMail";

/**
 * Send an email via Microsoft Graph API
 * @param {object} msalInstance - MSAL instance
 * @param {object} account - Active account
 * @param {object} emailData - Email data { to, subject, body, attachments }
 * @returns {Promise<boolean>} - Success status
 */
export async function sendMailViaGraph(msalInstance, account, emailData) {
    try {
        // Get access token for Mail.Send
        const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: ["Mail.Send"],
            account: account
        });

        const accessToken = tokenResponse.accessToken;

        // Build email message
        const message = {
            message: {
                subject: emailData.subject,
                body: {
                    contentType: "HTML",
                    content: emailData.body
                },
                toRecipients: [
                    {
                        emailAddress: {
                            address: emailData.to
                        }
                    }
                ]
            },
            saveToSentItems: true
        };

        // Add attachments if provided
        if (emailData.attachments && emailData.attachments.length > 0) {
            message.message.attachments = emailData.attachments.map(att => ({
                "@odata.type": "#microsoft.graph.fileAttachment",
                name: att.filename,
                contentType: "text/plain",
                contentBytes: btoa(unescape(encodeURIComponent(att.content))) // Base64 encode with UTF-8 support
            }));
        }

        // Send email
        const response = await fetch(GRAPH_MAIL_ENDPOINT, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(message)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error("Graph API error:", errorData);
            throw new Error(errorData.error?.message || `HTTP ${response.status}`);
        }

        console.log("Email sent successfully via Graph API");
        return true;

    } catch (error) {
        // If silent token acquisition fails, try interactive
        if (error.name === "InteractionRequiredAuthError") {
            try {
                const tokenResponse = await msalInstance.acquireTokenPopup({
                    scopes: ["Mail.Send"],
                    account: account
                });
                
                // Retry sending with new token
                return sendMailViaGraph(msalInstance, account, emailData);
            } catch (popupError) {
                console.error("Failed to acquire token via popup:", popupError);
                throw popupError;
            }
        }
        
        console.error("Failed to send email via Graph:", error);
        throw error;
    }
}

/**
 * Build HTML email content for SRT results
 * @param {object} options - { feedback, frameioLink, aspectRatio, hasFcpxml }
 */
export function buildSRTEmailHTML(options = {}) {
    const { feedback, frameioLink, aspectRatio, hasFcpxml } = typeof options === 'string' 
        ? { feedback: options } // Backwards compatibility
        : options;
    
    return `
        <html>
            <body style="font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto;">
                <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; border-radius: 10px 10px 0 0;">
                    <h1 style="color: white; margin: 0; font-size: 24px;">🎬 Je SRT ondertiteling is klaar!</h1>
                    ${aspectRatio ? `<p style="color: rgba(255,255,255,0.8); margin: 10px 0 0 0;">Formaat: ${aspectRatio}</p>` : ''}
                </div>
                
                <div style="background: #f8f9fa; padding: 30px; border-radius: 0 0 10px 10px;">
                    <p style="font-size: 16px;">Hallo,</p>
                    <p>Je ondertiteling is succesvol gegenereerd met AI. De bestanden zijn als bijlage toegevoegd.</p>
                    
                    ${frameioLink ? `
                    <div style="background: #e3f2fd; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #2196f3;">
                        <h3 style="margin-top: 0; color: #1565c0;">🎬 Frame.io Preview</h3>
                        <p style="margin: 0 0 15px 0; color: #1565c0;">Je video met burned-in ondertitels staat klaar voor review:</p>
                        <a href="${frameioLink}" style="display: inline-block; background: #2196f3; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
                            Bekijk in Frame.io →
                        </a>
                    </div>
                    ` : ''}
                    
                    ${feedback ? `
                    <div style="background: #fff3cd; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #ffc107;">
                        <h3 style="margin-top: 0; color: #856404;">💡 AI Feedback:</h3>
                        <pre style="white-space: pre-wrap; font-family: inherit; margin: 0; color: #856404;">${feedback}</pre>
                    </div>
                    ` : ''}
                    
                    <div style="background: white; padding: 20px; border-radius: 8px; margin: 20px 0;">
                        <h3 style="margin-top: 0;">📎 Bijlagen:</h3>
                        <ul style="margin: 0; padding-left: 20px;">
                            <li><strong>ondertiteling_origineel.srt</strong> - Directe transcriptie van Whisper</li>
                            <li><strong>ondertiteling_gecorrigeerd.srt</strong> - Gecontroleerd en gecorrigeerd door AI</li>
                            ${hasFcpxml ? '<li><strong>ondertiteling.fcpxml</strong> - Final Cut Pro XML</li>' : ''}
                        </ul>
                    </div>
                    
                    <p style="color: #666; font-size: 0.85em; margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
                        Dit bericht is automatisch gegenereerd door Full Frame SRT Generator.
                    </p>
                </div>
            </body>
        </html>
    `;
}

export default {
    sendMailViaGraph,
    buildSRTEmailHTML
};

